'use client';
import React, { createContext, useContext, useState, useCallback, useRef, ReactNode, RefObject } from 'react';
import { PageContentTypes } from '@fm-nx/constants';

export type StickyButtonConfig = {
    text: string;
    linkPath: string;
    buttonType: 'solid' | 'outline-dark' | 'outline-white';
    // Optional page reference — when present, the link resolves to the page's
    // URL (via pageType + slug) instead of the raw linkPath.
    slug?: string;
    pageType?: keyof typeof PageContentTypes;
};

export type StickyZone = {
    id: string;
    ref: RefObject<HTMLDivElement | null>;
    config: StickyButtonConfig;
};

type StickyButtonContextValue = {
    defaultConfig: StickyButtonConfig | null;
    setDefaultConfig: (config: StickyButtonConfig | null) => void;
    activeConfig: StickyButtonConfig | null;
    setActiveConfig: (config: StickyButtonConfig | null) => void;
    isActive: boolean;
    isHidden: boolean;
    setIsHidden: (hidden: boolean) => void;
    zones: StickyZone[];
    registerZone: (zone: StickyZone) => void;
    unregisterZone: (id: string) => void;
};

const StickyButtonContext = createContext<StickyButtonContextValue | null>(null);

export const useStickyButton = () => useContext(StickyButtonContext);

export const StickyButtonProvider = ({ children }: { children: ReactNode }) => {
    const [defaultConfig, setDefaultConfig] = useState<StickyButtonConfig | null>(null);
    const [activeConfig, setActiveConfig] = useState<StickyButtonConfig | null>(null);
    const [isHidden, setIsHidden] = useState(false);
    const zonesRef = useRef<StickyZone[]>([]);
    const [zones, setZones] = useState<StickyZone[]>([]);

    const registerZone = useCallback((zone: StickyZone) => {
        zonesRef.current = [...zonesRef.current.filter((z) => z.id !== zone.id), zone];
        setZones([...zonesRef.current]);
    }, []);

    const unregisterZone = useCallback((id: string) => {
        zonesRef.current = zonesRef.current.filter((z) => z.id !== id);
        setZones([...zonesRef.current]);
    }, []);

    return (
        <StickyButtonContext.Provider
            value={{
                defaultConfig,
                setDefaultConfig,
                activeConfig,
                setActiveConfig,
                isActive: defaultConfig !== null,
                isHidden,
                setIsHidden,
                zones,
                registerZone,
                unregisterZone,
            }}
        >
            {children}
        </StickyButtonContext.Provider>
    );
};

export default StickyButtonProvider;
